import {createFileRoute, useRouter} from '@tanstack/react-router';
import {useEffect, useState} from 'react';

import {AuthService} from '~api/auth.service';
import {
  loginRedirectQueryParam,
  loginStatusQueryParam,
  replaceRoute,
  Route as RouteEnum,
  useQueryParam,
} from '~utils/routeUtil';
import {selectCurrentProjectId, selectLandingPage} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {Spinner} from '~components/common/Spinner';

export const Route = createFileRoute('/')({
  component: IndexRouteComponent,
});

function IndexRouteComponent() {
  const router = useRouter();
  const projectID = useAppSelector(selectCurrentProjectId);
  const userLandingPage = useAppSelector(selectLandingPage);
  const [redirectUrl] = useQueryParam(loginRedirectQueryParam);
  const [logoutStatus] = useQueryParam(loginStatusQueryParam);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const handleRedirect = async () => {
      // Use router.state.location if available, otherwise fallback to window.location for test environments
      const location =
        router.state?.location ||
        (typeof window !== 'undefined'
          ? {
              pathname: window.location.pathname,
              search: window.location.search,
              hash: window.location.hash,
              href: window.location.href,
            }
          : null);

      if (!location) {
        return;
      }

      // If we're coming from a logout flow, clear any remaining token and stay on index
      // This prevents the user from being redirected back to a logged-in state
      if (logoutStatus === 'logout') {
        if (AuthService.isLoggedIn()) {
          localStorage.removeItem('token');
          // Clear the logout status from URL to prevent loops
          await replaceRoute(RouteEnum.index, {
            query: {},
          });
        }
        return;
      }

      const isLoggedIn = AuthService.isLoggedIn();

      if (isLoggedIn) {
        if (redirectUrl && typeof redirectUrl === 'string') {
          if (redirectUrl.startsWith('http')) {
            window.location.replace(redirectUrl);
            return;
          }

          try {
            const parsed = new URL(redirectUrl, window.location.origin);
            const path = parsed.pathname || RouteEnum.archive;
            const params = Object.fromEntries(parsed.searchParams.entries());
            await replaceRoute(path, {query: params});
          } catch {
            await router.navigate({to: redirectUrl as any, replace: true});
          }
        } else {
          const destination = userLandingPage ? `/${userLandingPage.toLowerCase()}` : RouteEnum.archive;
          await replaceRoute(destination, {
            query: {
              ...(location.search
                ? typeof location.search === 'string'
                  ? {}
                  : (location.search as Record<string, string>)
                : {}),
              projectID: projectID ?? undefined,
            },
          });
        }
      } else if (!window.location.href.includes('503')) {
        const authApiBaseUrl = import.meta.env.VITE_AUTH_API;
        if (!isRedirecting) {
          setIsRedirecting(true);
          if (authApiBaseUrl) {
            const origin = window.location.origin;
            const loginUrl = origin
              ? `${authApiBaseUrl}/auth/login/wos?origin=${encodeURIComponent(origin)}`
              : `${authApiBaseUrl}/auth/login/wos`;
            window.location.replace(loginUrl);
          }
        }
      } else {
        await replaceRoute(RouteEnum.apiUnavailable);
      }
    };

    handleRedirect();
  }, [router, redirectUrl, userLandingPage, projectID, isRedirecting, logoutStatus]);

  if (isRedirecting) {
    return <Spinner fullScreen />;
  }

  return null;
}
